import './Dropdown.scss';
import clsx from 'clsx';
import React, { memo, ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { useOutsideClick } from 'src/modules/common/hooks/useOutsideClick';
import { CheckIcon } from 'src/modules/common/icons/CheckIcon';
import { ChevronDownIcon } from 'src/modules/common/icons/ChevronDownIcon';
import { ChevronUpIcon } from 'src/modules/common/icons/ChevronUpIcon';

export type DropdownProps<O> = {
  readonly options: ReadonlyArray<O>;
  readonly onChange: (option: O) => void;
  readonly selectedOption: O | null;
  readonly id?: string;
  readonly name?: string;
  readonly getOptionKey?: (option: O) => string;
  readonly onBlur?: () => void;
  readonly label?: ReactNode;
  readonly required?: boolean;
  readonly description?: ReactNode;
  readonly invalid?: boolean;
  readonly invalidText?: ReactNode;
  readonly theme?: 'white' | 'gray';
  readonly renderOption?: (option: O) => ReactNode;
  readonly renderSelectedOption?: (option: O) => ReactNode;
  readonly icon?: ReactNode;
  readonly size?: 'sm' | 'md' | 'lg' | 'xlg';
  readonly direction?: 'up' | 'down';
  readonly disabled?: boolean;
  readonly readOnly?: boolean;
};

// eslint-disable-next-line react/function-component-definition
function DropdownWithoutMemo<O>({
  id,
  name,
  label,
  description,
  theme = 'gray',
  required,
  invalid,
  invalidText,
  icon,
  selectedOption,
  options,
  size = 'md',
  direction = 'down',
  disabled,
  readOnly,
  onChange,
  renderOption = (option: O): string => String(option),
  renderSelectedOption,
  getOptionKey = String,
  onBlur,
}: DropdownProps<O>): ReactNode {
  const [listOpened, setListOpened] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const outsideClickHander = useCallback(() => {
    onBlur?.();
    setListOpened(false);
  }, [onBlur]);

  useOutsideClick(ref, outsideClickHander);

  const helperText = invalid ? invalidText || description : description;

  const isOnlyOneOption = selectedOption !== null && options.length === 1;

  const chevron = useMemo(() => {
    if (isOnlyOneOption) {
      return null;
    }

    return (
      listOpened
        ? <ChevronUpIcon/>
        : <ChevronDownIcon/>
    );
  }, [isOnlyOneOption, listOpened]);

  const handleSelect = (option: O): void => {
    onChange(option);
    setListOpened(false);
  };

  const handleOpen = (): void => {
    if (isOnlyOneOption || disabled) {
      return;
    }

    setListOpened((prev) => !prev);
  };

  const handleRenderSelectedOption = renderSelectedOption || renderOption;

  return (
    <div className={clsx({
      'bp-dropdown': true,
      'bp-dropdown--disabled': disabled,
      'bp-dropdown--readonly': readOnly,
    })}
    >
      {label && (
        <div className={clsx({
          'bp-dropdown__label': true,
          'bp-dropdown__label--disabled': disabled,
        })}
        >
          {label}

          {required && (
            <sup className={clsx({
              'bp-dropdown__required': true,
              'bp-dropdown__required--disabled': disabled,
            })}
            >*
            </sup>
          )}
        </div>
      )}

      <div className="bp-dropdown__container" ref={ref}>
        <button
          id={id}
          name={name}
          type="button"
          className={clsx({
            'bp-dropdown__value': true,
            'bp-dropdown__value--single': isOnlyOneOption,
            'bp-dropdown__value--disabled': disabled,
            'bp-dropdown__value--invalid': invalid,
            [`bp-dropdown__value--${theme}`]: true,
            [`bp-dropdown__value--${size}`]: true,
          })}
          onClick={handleOpen}
        >
          <div className="bp-dropdown__selected-option">
            {selectedOption ? handleRenderSelectedOption(selectedOption) : null}
          </div>

          <div className="bp-dropdown__value-icon">
            {icon || chevron}
          </div>
        </button>

        <div className={clsx({
          'bp-dropdown__list': true,
          'bp-dropdown__list--opened': listOpened,
          'bp-dropdown__list--drop-up': direction === 'up',
          [`bp-dropdown__list--${theme}`]: true,
        })}
        >
          {options.map((option) => (
            <button
              key={getOptionKey(option)}
              type="button"
              className={clsx({
                'bp-dropdown__option': true,
                [`bp-dropdown__option--${theme}`]: true,
                'bp-dropdown__option--selected': selectedOption !== null &&
                  getOptionKey(selectedOption) === getOptionKey(option),
              })}
              onClick={() => handleSelect(option)}
            >
              {renderOption(option)}

              {selectedOption !== null && getOptionKey(selectedOption) === getOptionKey(option) && (
                <span className="bp-dropdown__option-icon">
                  <CheckIcon/>
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {helperText && (
        <div className={clsx({
          'bp-dropdown__description': true,
          'bp-dropdown__description--disabled': disabled,
          'bp-dropdown__description--invalid': invalid,
        })}
        >
          {helperText}
        </div>
      )}
    </div>
  );
}

const typedMemo: <T>(c: T) => T = memo;
export const Dropdown = typedMemo(DropdownWithoutMemo);
